import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TransactionCandles from './TransactionCandles';
import TransactionList from './TransactionList';
import {
  DetailCompactTransaction,
  Subscription,
} from '../../../../types/subscription';
import {
  findTransactionWave,
  formatRealTransactionsToGenericTransactions,
  isCustomTransaction,
} from '../../../../helpers/bundleTransactionsToCompactForm';
import { calculateStartOfWaveTimestamp } from '../../../../helpers/calculateStartOfWaveTimestamp';
import { CustomTransaction } from 'grts-utils/types/Transaction';
import { PriceChartApiResponse } from '../../../Detail/Charts';
import { Bot } from 'grts-utils/types/Bot';

import './index.css';
import { ComponentDimensions } from '../../../../types/Layout';
import { GenericTransactionInfo } from '../../../../types/Transaction';

interface Props {
  subscription: Subscription;
  bot: Bot | undefined;
  higherTCandles: PriceChartApiResponse | undefined;
  shorterTCandles: PriceChartApiResponse | undefined;
  multiTCandles: PriceChartApiResponse | undefined;
  isLoading: boolean;
  setShouldFetchCandles: React.Dispatch<React.SetStateAction<boolean>>;
}

const Transactions = (props: Props) => {
  const {
    bot,
    subscription,
    higherTCandles,
    shorterTCandles,
    multiTCandles,
    isLoading,
    setShouldFetchCandles,
  } = props;

  useEffect(() => {
    setShouldFetchCandles(true);
  }, [setShouldFetchCandles]);

  const compactTransactions = useMemo(
    () =>
      subscription.compactTransactions
        ? formatRealTransactionsToGenericTransactions(
            subscription.compactTransactions,
            subscription.baseAsset,
            subscription.quoteAsset,
          ).sort((a, b) => a.transactionIndex - b.transactionIndex)
        : [],
    [
      subscription.baseAsset,
      subscription.compactTransactions,
      subscription.quoteAsset,
    ],
  );

  // const [profitCalcsTransactions, setProfitCalcsTransactions] = useState<
  //   Transaction[] | undefined
  // >();
  // const [compactProfitCalcsTransactions, setCompactProfitCalcsTransactions] =
  //   useState<Transaction[] | undefined>([]);
  // const [profitCalcsCurrentProfit, setProfitCalcsCurrentProfit] = useState<
  //   CurrentProfit | undefined
  // >();

  // const [latestBotStateProfitData, setLatestBotStateProfitData] = useState<
  //   Array<LatestBotState> | undefined
  // >();

  const compactTransactionWithoutCustomTransactions = useMemo(() => {
    return (compactTransactions
      ?.filter((t) => !isCustomTransaction(t))
      ?.sort(
        (a, b) =>
          (b as GenericTransactionInfo).transactionTimestamp -
          (a as GenericTransactionInfo).transactionTimestamp,
      ) ?? []) as GenericTransactionInfo[];
  }, [compactTransactions]);

  const compactTransactionOnlyCustomTransactions = useMemo(() => {
    return (compactTransactions
      ?.filter((t) => isCustomTransaction(t))
      ?.sort((a, b) => b.transactionTimestamp - a.transactionTimestamp) ??
      []) as CustomTransaction[];
  }, [compactTransactions]);

  const getCandles = useCallback(
    (transaction: GenericTransactionInfo) => {
      if (
        !higherTCandles?.heikin ||
        !shorterTCandles?.heikin.length ||
        !multiTCandles?.heikin.length
      ) {
        return undefined;
      }

      /**
       * De trade in het midden
       * Links en rechts de vorige en volgende trade (compact)
       */

      let startTimestamp, endTimestamp;
      const compactTransactionId = transaction.compactTransactionId;

      const compactTransactionIndex =
        compactTransactionWithoutCustomTransactions.findIndex(
          (t) => t.compactTransactionId === compactTransactionId,
        );

      if (compactTransactionIndex === 0) {
        endTimestamp = transaction.transactionTimestamp;
      } else {
        const nextCompactTransaction =
          compactTransactionWithoutCustomTransactions[
            compactTransactionIndex - 1
          ];

        if (!!nextCompactTransaction) {
          endTimestamp = nextCompactTransaction.transactionTimestamp;
        } else {
          endTimestamp = new Date().valueOf();
        }
      }

      if (
        compactTransactionIndex ===
        compactTransactionWithoutCustomTransactions.length - 1
      ) {
        startTimestamp = transaction.transactionTimestamp;
      } else {
        const previousCompactTransaction =
          compactTransactionWithoutCustomTransactions[
            compactTransactionIndex + 1
          ];
        startTimestamp = previousCompactTransaction.transactionTimestamp;
      }

      const waveStartTimstamp = calculateStartOfWaveTimestamp(
        transaction.transactionTimestamp,
        higherTCandles?.heikin,
        transaction.orderType,
      );

      const higherTransactionWave = findTransactionWave(
        higherTCandles?.heikin,
        startTimestamp,
        endTimestamp,
      );

      const start = !!higherTransactionWave?.length
        ? higherTransactionWave[0].startTimestamp
        : startTimestamp;
      const end = !!higherTransactionWave?.length
        ? higherTransactionWave[higherTransactionWave.length - 1].endTimestamp
        : endTimestamp;

      return {
        higher: findTransactionWave(
          higherTCandles?.heikin,
          startTimestamp,
          endTimestamp,
        ),
        shorter: shorterTCandles.heikin.filter(
          (c) => start <= c.startTimestamp && c.endTimestamp <= end,
        ),
        multi: multiTCandles?.heikin.filter(
          (c) =>
            waveStartTimstamp <= c.startTimestamp &&
            c.startTimestamp <=
              transaction.transactionTimestamp +
                5 * (c.endTimestamp - c.startTimestamp),
        ),
      };
    },
    [
      compactTransactionWithoutCustomTransactions,
      higherTCandles?.heikin,
      multiTCandles?.heikin,
      shorterTCandles?.heikin,
    ],
  );

  const compactTransactionsWithCandles: Array<
    GenericTransactionInfo | CustomTransaction
  > = useMemo(() => {
    return compactTransactionWithoutCustomTransactions
      .map((t) => {
        return { ...t, candles: getCandles(t) };
      })
      .sort((a, b) => a.transactionTimestamp - b.transactionTimestamp)
      .map((t, index) => {
        return { ...t, index: index + 1 };
      });
  }, [compactTransactionWithoutCustomTransactions, getCandles]);

  const finalTransactions = useMemo(() => {
    return ([
      ...compactTransactionsWithCandles,
      ...compactTransactionOnlyCustomTransactions,
    ].sort((a, b) => b.transactionTimestamp - a.transactionTimestamp) ??
      []) as CustomTransaction[];
  }, [
    compactTransactionOnlyCustomTransactions,
    compactTransactionsWithCandles,
  ]);

  const bundledTransactions = useMemo(() => {
    if (!compactTransactionWithoutCustomTransactions) {
      return {};
    }

    const bundledTransactions: Record<string, GenericTransactionInfo[]> = {};

    for (
      let i = 0;
      i < compactTransactionWithoutCustomTransactions.length;
      i++
    ) {
      const transaction = compactTransactionWithoutCustomTransactions[i];
      const transactionTimestamp = transaction.transactionTimestamp;

      /** We need the multiOrder candles now */
      const candle = multiTCandles?.normal.find(
        (candle) =>
          candle.startTimestamp <= transactionTimestamp &&
          transactionTimestamp <= candle.endTimestamp,
      );
      const startTimestamp = candle?.startTimestamp.toString();

      if (!startTimestamp) {
        continue;
      }

      if (typeof bundledTransactions[startTimestamp] === 'undefined') {
        bundledTransactions[startTimestamp] = [transaction];
        continue;
      } else {
        bundledTransactions[startTimestamp].push(transaction);
      }
    }

    return bundledTransactions;
  }, [multiTCandles?.normal, compactTransactionWithoutCustomTransactions]);

  /** We don't have the bot here, should we fetch this information with the sub? */
  const currentComplexParams = useMemo(() => {
    if (!bot?.complexParams || !bot?.timeFrame) {
      return undefined;
    }

    return {
      complexParams: bot.complexParams,
      timeFrames: bot.timeFrame,
      indicatorConfig: undefined,
    };
  }, [bot]);

  // const complexParamsBundlesByTimestamp = useMemo(() => {
  //   if (
  //     currentComplexParams === undefined ||
  //     !compactTransactionWithoutCustomTransactions?.length
  //   ) {
  //     return [];
  //   }

  //   const complexParamsBundlesByTimestamp = [];

  //   /**
  //    * Each compact transaction
  //    * Compare to latestComplexParamsBundle
  //    * If different
  //    * Save the latestComplexParamsBundle with the startDate of the endDate of the compactTransaction
  //    * Continue
  //    * If the compact transaction does not have complexParamsBundle
  //    * Add the latestComplexParamsBundle and break
  //    * If last one, push also
  //    */

  //   let currentcomplexParamsBundle = currentComplexParams;
  //   let endTimestamp = new Date().valueOf();

  //   for (let i = 0; i < compactTransactionWithoutCustomTransactions.length; i++) {
  //     const compactTransaction = compactTransactionWithoutCustomTransactions[i];

  //     if (!compactTransaction.complexParams || !compactTransaction.timeFrames) {
  //       complexParamsBundlesByTimestamp.push({
  //         complexParamsBundle: currentcomplexParamsBundle,
  //         startTimestamp: compactTransaction.transactionTimestamp,
  //         latestOrder: compactTransaction.orderType,
  //         endTimestamp,
  //       });
  //       break;
  //     }

  //     const complexParamsBundle = {
  //       complexParams: compactTransaction.complexParams,
  //       timeFrames: compactTransaction.timeFrames,
  //     };

  //     const hasSameComplexParamsBundle = compareComplexParamsBundle(
  //       complexParamsBundle,
  //       compactTransaction,
  //     );

  //     if (!hasSameComplexParamsBundle) {
  //       complexParamsBundlesByTimestamp.push({
  //         complexParamsBundle: complexParamsBundle,
  //         startTimestamp: compactTransaction.transactionTimestamp,
  //         endTimestamp,
  //         latestOrder: compactTransaction.orderType,
  //       });

  //       currentcomplexParamsBundle = complexParamsBundle;
  //       endTimestamp = compactTransaction.transactionTimestamp;
  //     }
  //   }

  //   return complexParamsBundlesByTimestamp;
  // }, [currentComplexParams, compactTransactionWithoutCustomTransactions]);

  // useEffect(() => {
  //   if (
  //     isLoading ||
  //     profitCalcsTransactions ||
  //     !bot.complexParams ||
  //     !candles?.heikin ||
  //     !isCompareToProfitCalcs ||
  //     !complexParamsBundlesByTimestamp?.length
  //   ) {
  //     return;
  //   }

  //   /** We want to calculate current complex param transactions */

  //   const fetchTransactions = async () => {
  //     setIsLoading(true);

  //     for (const complexParamsBundleByTimestamp of complexParamsBundlesByTimestamp) {
  //       const params = {
  //         baseAsset: bot.baseAsset,
  //         quoteAsset: bot.quoteAsset,
  //         exchange: bot.defaultExchange,
  //         startDate: calculateStartOfWaveTimestamp(
  //           complexParamsBundleByTimestamp.startTimestamp,
  //           candles.heikin,
  //           undefined,
  //         ),
  //         endDate: complexParamsBundleByTimestamp.endTimestamp,
  //         higherTimeFrame:
  //           complexParamsBundleByTimestamp.complexParamsBundle.timeFrames
  //             .higher,
  //         shorterTimeFrame:
  //           complexParamsBundleByTimestamp.complexParamsBundle.timeFrames
  //             .shorter,
  //         multiOrderTimeFrame:
  //           complexParamsBundleByTimestamp.complexParamsBundle.timeFrames
  //             .multiOrder,
  //         cbrt: complexParamsBundleByTimestamp.complexParamsBundle.complexParams
  //           .cbrt,
  //         shorterTimeFrameInterval:
  //           complexParamsBundleByTimestamp.complexParamsBundle.complexParams
  //             .shorterTimeFrameInterval,
  //         supportCbrtRange:
  //           complexParamsBundleByTimestamp.complexParamsBundle.complexParams
  //             .supportCbrtRange,
  //         supportMrcState:
  //           complexParamsBundleByTimestamp.complexParamsBundle.complexParams
  //             .supportMrcState,
  //         latestOrder: complexParamsBundleByTimestamp.latestOrder,
  //       };

  //       const { data }: { data: SingleProfitCalculationApiResponse } =
  //         await axios({
  //           method: 'POST',
  //           url: ServicePaths.PROFIT_CALCULATIONS.GetSingleProfitCalculation,
  //           data: params,
  //         });

  //       const [pr, compact] = fillTransactionIndexes(
  //         formatTransactionType(data.transactions, bot),
  //         formatTransactionType(
  //           bundleTransactionsToCompactForm(data.transactions),
  //           bot,
  //         ),
  //       );

  //       setProfitCalcsTransactions((prev) =>
  //         [...(prev ?? []), ...pr].sort(
  //           (a, b) => a.transactionTimestamp - b.transactionTimestamp,
  //         ),
  //       );
  //       setCompactProfitCalcsTransactions((prev) =>
  //         [...(prev ?? []), ...compact].sort(
  //           (a, b) => a.transactionTimestamp - b.transactionTimestamp,
  //         ),
  //       );

  //       if (!profitCalcsCurrentProfit) {
  //         setProfitCalcsCurrentProfit({
  //           currentProfit: data.currentProfit,
  //           currentPriceChange: data.currentPriceChange,
  //         });
  //       }

  //       setIsLoading(false);
  //     }
  //   };

  //   fetchTransactions();
  // }, [
  //   bot,
  //   candles?.heikin,
  //   complexParamsBundlesByTimestamp,
  //   isCompareToProfitCalcs,
  //   isLoading,
  //   profitCalcsCurrentProfit,
  //   profitCalcsTransactions,
  // ]);

  /**
   * We need to fetch multiple profit calcs huhuuu
   *
   * Start timestamp = latest transaction
   * End timestamp = now
   *
   * Fetch the complex params archive
   * The first one needs to start 1 before the start timestamp
   *
   * For each record,
   * we'll have to do a profit calcs
   *
   * Do this in one data call
   */

  // useEffect(() => {
  //   if (
  //     !!latestBotStateProfitData
  //     // latestBotStateProfitData.baseAsset === bot.baseAsset
  //   ) {
  //     return;
  //   }

  //   const fetch = async () => {
  //     if (
  //       !transactions?.allTransactions.length ||
  //       !bot.complexParams ||
  //       !bot.timeFrame
  //     ) {
  //       return;
  //     }

  //     /** To have the cbrt range states and mrc states, we need to do a profit calc */
  //     /**
  //      * We should start at the latest transaction
  //      * But filter the candles from latest wave
  //      */

  //     const params = {
  //       botId: bot.botId,
  //     };

  //     const { data }: { data: Array<LatestBotState> } = await axios({
  //       method: 'POST',
  //       url: ServicePaths.DATA.GetClientLatestBotState,
  //       data: params,
  //     });

  //     setLatestBotStateProfitData(data);
  //   };

  //   fetch();
  // }, [
  //   bot.baseAsset,
  //   bot.botId,
  //   bot.complexParams,
  //   bot.defaultExchange,
  //   bot.quoteAsset,
  //   bot.timeFrame,
  //   latestBotStateProfitData,
  //   transactions?.allTransactions,
  // ]);

  // const latestBotState = useMemo(() => {
  //   /** Combine all the arrays in 1 here */

  //   if (!latestBotStateProfitData?.length) {
  //     return undefined;
  //   }

  //   const lastBotState =
  //     latestBotStateProfitData[latestBotStateProfitData.length - 1];

  //   const candles = latestBotStateProfitData
  //     .reduce((acc, curr) => [...acc, ...curr.candles], [] as Candle[])
  //     .filter(
  //       (c, i, s) =>
  //         s.findIndex((sc) => sc.startTimestamp === c.startTimestamp) === i,
  //     );
  //   const wakeUpTimes = latestBotStateProfitData.reduce(
  //     (acc, curr) => [...acc, ...curr.wakeUpTimes],
  //     [] as number[],
  //   );
  //   const cbrtRangeStates = latestBotStateProfitData.reduce(
  //     (acc, curr) => [...acc, ...curr.cbrtRangeStates],
  //     [] as CbrtRangeState[],
  //   );
  //   const mrcStates = latestBotStateProfitData.reduce(
  //     (acc, curr) => [...acc, ...curr.mrcStates],
  //     [] as MrcState[],
  //   );
  //   const transactions = latestBotStateProfitData.reduce(
  //     (acc, curr) => [...acc, ...curr.transactions],
  //     [] as any[],
  //   );

  //   return {
  //     orderType: lastBotState.orderType,
  //     currentProfit: lastBotState.currentProfit,
  //     currentPriceChange: lastBotState.currentPriceChange,
  //     candles,
  //     wakeUpTimes,
  //     cbrtRangeStates,
  //     mrcStates,
  //     transactions,
  //     bot: lastBotState.bot,
  //     baseAsset: lastBotState.baseAsset,
  //     quoteAsset: lastBotState.quoteAsset,
  //     timeFrame: lastBotState.timeFrame,
  //   } as LatestBotState;
  // }, [latestBotStateProfitData]);

  // const complexParamsChangeTimestamps = useMemo(
  //   () =>
  //     latestBotStateProfitData
  //       ? [...latestBotStateProfitData]
  //           .slice(1)
  //           .map((s) => s.startTimestamp || 0)
  //       : [],
  //   [latestBotStateProfitData],
  // );
  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!!rootDimensions) {
      return;
    }

    if (!rootRef.current) {
      return;
    }

    const rect = rootRef.current.getBoundingClientRect();

    setRootDimensions({
      height: rect.height * 0.95,
      width: rect.width,
    });
  }, [rootDimensions]);

  const visibleCandles = useMemo(() => {
    const earliestStartTimestamp = compactTransactions[0].transactionTimestamp;
    const now = new Date().valueOf();

    if (now - earliestStartTimestamp < 1000 * 60 * 60 * 3) {
      // Shorter than three hour
      return multiTCandles;
    } else if (now - earliestStartTimestamp < 1000 * 60 * 60 * 12) {
      // Shorter than 3 days
      return shorterTCandles;
    }

    return higherTCandles;
  }, [compactTransactions, higherTCandles, multiTCandles, shorterTCandles]);

  if (!bot) {
    return null;
  }

  return (
    <div
      className="detail-transactions-root"
      ref={rootRef}
      style={{
        opacity: rootDimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
        width: '100%',
        flex: 1,
      }}
    >
      {rootDimensions && (
        <>
          <TransactionCandles
            rootDimensions={rootDimensions}
            timeFrame={bot?.timeFrame?.higher}
            candles={visibleCandles}
            genericTransactions={compactTransactions}
            isLoading={isLoading}
            multiOrderCandles={multiTCandles}
            bundledTransactions={bundledTransactions}
            candleSticksLatestOrder={undefined}
            baseAsset={subscription.baseAsset}
            quoteAsset={subscription.quoteAsset}
            exchange={subscription.exchange}
          />
          <TransactionList
            transactions={finalTransactions}
            isLoading={isLoading}
            compactTransactionLength={subscription?.compactTransactions?.length}
            currentComplexParams={currentComplexParams}
            transactionsByTimestamp={bundledTransactions}
            currentProfit={subscription.currentProfit}
            currentPriceChange={subscription.currentPriceChange}
          />
        </>
      )}
    </div>
  );
};

export default Transactions;
